// createRole — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-role' })` op (DSE2 §3.F1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1 — Logins / Users / Roles UI.
//
// Per-engine surface (per DSE2 dialect.role):
//   - mssql:    `CREATE ROLE … [AUTHORIZATION owner]`
//   - postgres: `CREATE ROLE … [WITH login/password/superuser/createdb/createrole/inherit/replication/valid until]`
//   - mysql:    `CREATE ROLE [IF NOT EXISTS] 'name'[@'host']`
//   - oracle:   `CREATE ROLE … [{IDENTIFIED BY pwd | NOT IDENTIFIED}]`
//   - sqlite:   rejected — no auth model.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, RoleDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateRoleArgs {
  readonly role: RoleDef;
}

export function buildCreateRoleEdit(args: CreateRoleArgs): DdlEdit {
  return { op: 'create-role', role: args.role };
}

export async function commitCreateRole(
  ctx: VqeDdlEditContext,
  args: CreateRoleArgs,
): Promise<void> {
  const edit = buildCreateRoleEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const CREATE_ROLE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

export function isCreateRoleSupported(engine: SqlEngine): boolean {
  return CREATE_ROLE_SUPPORTED.has(engine);
}

export function getCreateRoleUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  return '';
}

export function validateCreateRoleForEngine(
  engine: SqlEngine,
  args: CreateRoleArgs,
): string | null {
  if (!isCreateRoleSupported(engine)) {
    return getCreateRoleUnsupportedTooltip(engine);
  }
  const r = args.role;
  if (r.name.trim().length === 0) {
    return 'Role name is required.';
  }
  if (engine !== 'mssql' && r.mssqlAuthorization) {
    return `${engine} does not accept AUTHORIZATION (mssql-only).`;
  }
  if (engine !== 'postgres' && (r.pgsqlLogin !== undefined || r.pgsqlPassword
      || r.pgsqlSuperuser !== undefined || r.pgsqlCreateDb !== undefined
      || r.pgsqlCreateRole !== undefined || r.pgsqlInherit !== undefined
      || r.pgsqlReplication !== undefined || r.pgsqlValidUntilText)) {
    return `${engine} does not accept PostgreSQL role attributes.`;
  }
  if (engine === 'postgres' && r.pgsqlPassword && r.pgsqlLogin !== true) {
    return 'PASSWORD requires LOGIN — only login-capable roles can have a password.';
  }
  if (engine !== 'mysql' && (r.ifNotExists || r.mysqlHost)) {
    return `${engine} does not accept mysql-only role options.`;
  }
  if (engine !== 'oracle' && (r.oracleIdentifiedBy || r.oracleNotIdentified)) {
    return `${engine} does not accept oracle-only role options.`;
  }
  if (engine === 'oracle' && r.oracleIdentifiedBy && r.oracleNotIdentified) {
    return 'Pick IDENTIFIED BY OR NOT IDENTIFIED, not both.';
  }
  return null;
}
